import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

/** Must match ADMIN_GROUP_NAME in src/tower-adapter-stack/adapter-stack.ts. */
const ADMIN_GROUP = 'admins';

/** Must match CAMPAIGN_UPLOADER_GROUP_NAME in src/tower-adapter-stack/adapter-stack.ts. */
const CAMPAIGN_UPLOADER_GROUP = 'campaign-uploaders';

export interface RoleFlags {
  isAdmin: boolean;
  isCampaignUploader: boolean;
  /** May create or update configurations. Campaign uploaders may not. */
  canWrite: boolean;
  /** May see the Campaigns pages and upload campaign data. */
  canUploadCampaigns: boolean;
}

/**
 * Turns the ID token's groups and admin claim into role flags.
 *
 * Mirrors hasPermission in src/common/rbac.ts: admins first, then campaign
 * uploaders, then everyone else. The API enforces the same rules; this only
 * decides what to show.
 */
export function deriveRoleFlags(groups: string[], adminClaim: unknown): RoleFlags {
  const isAdmin = groups.includes(ADMIN_GROUP) || adminClaim === 'true' || adminClaim === true;
  const isCampaignUploader = groups.includes(CAMPAIGN_UPLOADER_GROUP);

  return {
    isAdmin,
    isCampaignUploader,
    canWrite: isAdmin || !isCampaignUploader,
    canUploadCampaigns: isAdmin || isCampaignUploader,
  };
}

const NO_ACCESS: RoleFlags = {
  isAdmin: false,
  isCampaignUploader: false,
  canWrite: false,
  canUploadCampaigns: false,
};

/**
 * Hook that reads Cognito group membership, or the `custom:isAdmin` claim, from
 * the ID token payload and returns what the current user may do.
 *
 * While the session is resolving every capability is false, so write controls
 * are never shown to a user who turns out to be restricted.
 */
export function usePermissions(): RoleFlags & { isLoading: boolean } {
  const [flags, setFlags] = useState<RoleFlags>(NO_ACCESS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload;

        if (!cancelled) {
          const groups = Array.isArray(payload?.['cognito:groups'])
            ? (payload['cognito:groups'] as unknown[]).map(String)
            : [];
          setFlags(deriveRoleFlags(groups, payload?.['custom:isAdmin']));
        }
      } catch {
        if (!cancelled) {
          setFlags(NO_ACCESS);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return { ...flags, isLoading };
}
